import axios from 'axios';

// Use same-origin `/api` in dev so Create React App's `package.json` "proxy" can forward to
// Flask (avoids CORS and avoids `127.0.0.1` meaning "this machine" when you open the UI
// from another host or via localhost vs 127.0.0.1). Override with REACT_APP_API_URL when needed.
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const onLoginPage = window.location.pathname.startsWith('/login');
    if (status === 401 && !url.includes('/auth/login') && !url.includes('/auth/me') && !url.includes('/public/') && !onLoginPage) {
      const from = window.location.pathname + window.location.search;
      window.location.assign(`/login?from=${encodeURIComponent(from)}`);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  me: () => api.get('/auth/me'),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
};

export const marketingAPI = {
  betaSignup: (data) => api.post('/public/beta-signup', data),
};

// Research Reports API
export const researchAPI = {
  getReports: (params = {}) => api.get('/research/reports', { params }),
  getReport: (id) => api.get(`/research/reports/${id}`),
  patchReport: (reportId, data) => api.patch(`/research/reports/${reportId}`, data),
  approveIdeas: (reportId, ideaIds, extra = {}) =>
    api.post(`/research/reports/${reportId}/approve`, { idea_ids: ideaIds, ...extra }),
  rejectIdea: (reportId, ideaIndex, feedback) =>
    api.post(`/research/reports/${reportId}/reject`, { idea_index: ideaIndex, feedback }),
  triggerResearch: (profileId, body = undefined) =>
    api.post(`/research/trigger/${profileId}`, body ?? {}),
  triggerWizard: (body) => api.post('/research/trigger-wizard', body),
  getContentSources: () => api.get('/research/content-sources'),
  /** Focused research without a saved topic profile (body: `{ focus: { ... } }`). */
  triggerFocusedStandalone: (body) => api.post('/research/trigger-focused', body),
  deleteReport: (reportId) =>
    api.delete(`/research/reports/${reportId}`),
};

// Profiles API (research strategy / workspace entities; renamed from profilesAPI)
// NOTE: Backend must mount /api/profiles. Request/response payloads still use `profile_id`
// for the owner FK until the backend rename to `profile_id` lands. See DATA_CONTRACTS.md.
export const profilesAPI = {
  getProfiles: () => api.get('/profiles'),
  getProfile: (id) => api.get(`/profiles/${id}`),
  createProfile: (data) => api.post('/profiles', data),
  updateProfile: (id, data) => api.put(`/profiles/${id}`, data),
  deleteProfile: (id) => api.delete(`/profiles/${id}`),
};

// Content Queue API (for articles)
export const contentAPI = {
  getQueue: (params = {}) => api.get('/content/queue', { params }),
  getQueueItem: (id) => api.get(`/content/queue/${id}`),
  createQueueItem: (data) => api.post('/content/queue', data),
  /** Human-first: create research packet + draft (primary queue workflow). */
  createResearchBrief: (queueId, data = {}) =>
    api.post(`/content/queue/${queueId}/research-brief`, data),
  /** Propose a confirmable article angle before creating the brief (cheap, no Serp). */
  proposeArticleAngle: (queueId, data = {}) =>
    api.post(`/content/queue/${queueId}/propose-article-angle`, data),
  /** Legacy full-article generation (not exposed in primary UI). */
  generateDraft: (queueId, data = {}) => api.post(`/content/queue/${queueId}/generate`, data),
  updateQueueItem: (id, data) => api.put(`/content/queue/${id}`, data),
  deleteQueueItem: (id) => api.delete(`/content/queue/${id}`),
  getDrafts: (params = {}) => api.get('/content/drafts', { params }),
  getDraft: (id) => api.get(`/content/drafts/${id}`),
  updateDraft: (id, data) => api.put(`/content/drafts/${id}`, data),
  refreshResearchBrief: (draftId) =>
    api.post(`/content/drafts/${draftId}/refresh-research-brief`, {}),
  /** Rebuild outline from current sources + claims (one AI call, no Serp).
   *  Pass { article_angle } to re-steer the locked direction before regenerating. */
  regenerateOutlineFromResearch: (draftId, data = {}) =>
    api.post(`/content/drafts/${draftId}/regenerate-outline-from-research`, data),
  deleteDraft: (id) => api.delete(`/content/drafts/${id}`),
  listDraftSnapshots: (draftId) =>
    api.get(`/content/drafts/${draftId}/snapshots`),
  getDraftSnapshot: (draftId, snapshotId) =>
    api.get(`/content/drafts/${draftId}/snapshots/${snapshotId}`),
  createDraftSnapshot: (draftId, body) =>
    api.post(`/content/drafts/${draftId}/snapshots`, body),
  restoreDraftSnapshot: (draftId, snapshotId) =>
    api.post(`/content/drafts/${draftId}/snapshots/${snapshotId}/restore`, {}),
  pushDraftToWordPress: (draftId) =>
    api.post(`/content/drafts/${draftId}/publish/wordpress`, {}),
  markDraftPublishedManually: (draftId) =>
    api.post(`/content/drafts/${draftId}/publish/manual`, {}),
  /** Approve + generate publish_preflight pack (if missing). */
  sendToPublishingQueue: (draftId) =>
    api.post(`/content/drafts/${draftId}/send-to-publishing-queue`, {}),
  getPublishPreflight: (draftId) =>
    api.get(`/content/drafts/${draftId}/publish-preflight`),
  updatePublishPreflight: (draftId, publishPreflight) =>
    api.put(`/content/drafts/${draftId}/publish-preflight`, {
      publish_preflight: publishPreflight,
    }),
  regeneratePublishPreflight: (draftId) =>
    api.post(`/content/drafts/${draftId}/publish-preflight/regenerate`, {}),
};

export const publishingAPI = {
  getWordPressConnection: () => api.get('/publishing/wordpress/connection'),
  saveWordPressConnection: (body) => api.put('/publishing/wordpress/connection', body),
  deleteWordPressConnection: () => api.delete('/publishing/wordpress/connection'),
  testWordPressConnection: () => api.post('/publishing/wordpress/connection/test', {}),
};

// SEO Queue API (for content snippets/optimizations)
export const seoAPI = {
  getQueue: (params = {}) => api.get('/seo/queue', { params }),
  getQueueItem: (id) => api.get(`/seo/queue/${id}`),
  generateDraft: (queueId, data = {}) => api.post(`/seo/queue/${queueId}/generate`, data),
  updateQueueItem: (id, data) => api.put(`/seo/queue/${id}`, data),
  deleteQueueItem: (id) => api.delete(`/seo/queue/${id}`),
};

// Voice Profiles API
export const voiceProfilesAPI = {
  getProfiles: (params = {}) => api.get('/voice-profiles', { params }),
  getProfile: (id) => api.get(`/voice-profiles/${id}`),
  createProfile: (data) => api.post('/voice-profiles', data),
  updateProfile: (id, data) => api.put(`/voice-profiles/${id}`, data),
  deleteProfile: (id) => api.delete(`/voice-profiles/${id}`),
  analyzeProfile: (id) => api.post(`/voice-profiles/${id}/analyze`),
};

// Page Analysis API
export const pageAnalysisAPI = {
  getPages: (params = {}) => api.get('/page-analysis/pages', { params }),
  getPage: (id) => api.get(`/page-analysis/pages/${id}`),
  createPage: (data) => api.post('/page-analysis/pages', data),
  updatePage: (id, data) => api.put(`/page-analysis/pages/${id}`, data),
  deletePage: (id) => api.delete(`/page-analysis/pages/${id}`),
  analyzePage: (id) => api.post(`/page-analysis/pages/${id}/analyze`),
  getAnalysis: (id) => api.get(`/page-analysis/pages/${id}/analysis`),
  getAnalysisHistory: (id) => api.get(`/page-analysis/pages/${id}/analysis/history`),
  createOptimizationTask: (id, recommendationIndex, data = {}) => api.post(`/page-analysis/pages/${id}/create-optimization-task`, {
    recommendation_index: recommendationIndex,
    ...data
  }),
  createFullPageOptimizationTask: (id, data = {}) => api.post(`/page-analysis/pages/${id}/create-full-page-optimization-task`, data),
};

export default api;

